import { invariantResponse } from '@epic-web/invariant'
import { useEffect } from 'react'
import {
	type LoaderFunctionArgs,
	type MetaFunction,
	useLoaderData,
	useRouteLoaderData,
} from 'react-router'
import { ClientOnly } from 'remix-utils/client-only'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import ThreeSixtyImage from '#app/components/three-sixty-image.client.tsx'
import { useStaticFrame } from '#app/hooks/use-static-frame.ts'
import {
	base62Pattern,
	base62ToFloat,
	floatToBase62,
} from '#app/utils/base62.ts'
import { useSmartThumbnailCache } from '#app/utils/smart-cache.tsx'
import {
	FrameIdSchema,
	SlugSchema as CaptureSlugSchema,
} from '#app/utils/trail-capture-validation.ts'
import {
	CountrySchema,
	RegionSchema,
	SlugSchema as TrailSlugSchema,
	TypeSchema,
} from '#app/utils/trail-validation.ts'
import { type loader as parentLoader } from './$region.$type.$trail-slug.$capture-slug'

// PARENT ROUTE ID must match Remix's route id for the layout file:
const PARENT_ROUTE_ID =
	'routes/explore_+/$country+/$region.$type.$trail-slug.$capture-slug'

const PathParamsSchema = z.object({
	country: CountrySchema,
	region: RegionSchema,
	type: TypeSchema,
	'trail-slug': TrailSlugSchema,
	'capture-slug': CaptureSlugSchema,
	'frame-id': FrameIdSchema,
})

const SearchParamsSchema = z.object({
	'azimuth-angle': z
		.string()
		.length(6)
		.regex(base62Pattern)
		.transform(base62ToFloat)
		.optional(),
	'polar-angle': z
		.string()
		.length(6)
		.regex(base62Pattern)
		.transform(base62ToFloat)
		.optional(),
})

type LoaderData = {
	frameId: string
	azimuthAngle?: number
	polarAngle?: number
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const parsedParams = PathParamsSchema.safeParse(params)
	invariantResponse(
		parsedParams.success,
		`Invalid path parameters: ${parsedParams.error}`,
		{ status: 400 },
	)

	const url = new URL(request.url)
	const parsedSearch = SearchParamsSchema.safeParse(
		Object.fromEntries(url.searchParams),
	)
	invariantResponse(
		parsedSearch.success,
		`Invalid query parameters: ${parsedSearch.error}`,
		{ status: 400 },
	)

	return {
		frameId: parsedParams.data['frame-id'],
		azimuthAngle: parsedSearch.data['azimuth-angle'],
		polarAngle: parsedSearch.data['polar-angle'],
	} satisfies LoaderData
}

export const meta: MetaFunction<typeof loader> = ({ params }) => {
	const title = `View ${params['frame-id']} | ${params['capture-slug']} | ${params['trail-slug']} | ${params.country} | ${params.region} | ${import.meta.env.VITE_APP_TITLE}`
	const description = `360° view of ${params['frame-id']} from ${params['capture-slug']} on ${params['trail-slug']} in ${params.region}, ${params.country}.`
	return [
		{ title },
		{ name: 'description', content: description },
		{
			tagName: 'link',
			rel: 'canonical',
			href: `https://www.trailviewr.com/explore/${params.country}/${params.region}.${params.type}.${params['trail-slug']}.${params['capture-slug']}.${params['frame-id']}`,
		},
	]
}

export default function FrameView() {
	const { frameId, azimuthAngle, polarAngle } = useLoaderData<typeof loader>()

	const parent = useRouteLoaderData<typeof parentLoader>(PARENT_ROUTE_ID)
	invariantResponse(parent, 'Parent route data not found', { status: 500 })

	const { trail, trailCapture, captureId } = parent

	const { frameData: frame, error } = useStaticFrame(captureId, frameId)
	const { getSmartThumbnailUrl } = useSmartThumbnailCache()
	const smartThumbnailUrl = getSmartThumbnailUrl(trailCapture.id, frameId)

	useEffect(() => {
		if (error) throw error
	}, [error])

	const imageId = frame
		? 'images' in frame
			? frame.images['4096'].webp
			: `${frame.index}.webp`
		: undefined

	return (
		<ClientOnly
			fallback={
				<div className="relative h-full w-full overflow-hidden rounded-md bg-gray-200">
					<img
						src={smartThumbnailUrl}
						alt={`360° panorama of ${frameId} from ${trailCapture.slug} on ${trail.name ?? trail.slug}`}
						className="h-full w-full object-cover"
					/>
					<div className="absolute inset-0 flex items-center justify-center bg-black/40 font-semibold text-white">
						<div className="text-center">
							<div className="mb-2 text-2xl">360°</div>
							<div>Interactive View</div>
						</div>
					</div>
				</div>
			}
		>
			{() => (
				<ThreeSixtyImage
					className="h-full w-full"
					imageId={imageId}
					captureId={trailCapture.id}
					links={frame?.links || []}
					onViewMoved={(nextAzimuth, nextPolar) => {
						const url = new URL(window.location.href)
						url.searchParams.set('azimuth-angle', floatToBase62(nextAzimuth))
						url.searchParams.set('polar-angle', floatToBase62(nextPolar))
						window.history.replaceState(null, '', url.toString())
					}}
					azimuthAngle={azimuthAngle}
					polarAngle={polarAngle}
				/>
			)}
		</ClientOnly>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<div className="text-center">
						<p>Oops! Not found</p>
						<p className="mt-2 text-base">
							We're not able to locate the data for this frame.
						</p>
					</div>
				),
				503: () => (
					<div className="text-center">
						<p>Oops! Something went wrong</p>
						<p className="mt-2 text-base">
							We are having trouble loading this page. Please try again later.
						</p>
						<button
							className="mt-4 rounded bg-primary px-4 py-1 text-base text-primary-foreground hover:bg-primary/80"
							onClick={() => window.location.reload()}
						>
							Retry
						</button>
					</div>
				),
			}}
		/>
	)
}
